import api from '../lib/api'
import { API_URL } from '../config'
import * as Sentry from '@sentry/react'

function normalizeConversationDetail(payload) {
  if (!payload || typeof payload !== 'object') return payload
  const rawSlots = payload.slots && typeof payload.slots === 'object' ? payload.slots : {}
  const slots = {
    ...rawSlots,
    customer_name: rawSlots.customer_name ?? rawSlots.customer_display_name,
  }
  return {
    ...payload,
    messages: Array.isArray(payload.messages) ? payload.messages : [],
    slots,
  }
}

// B-FE6 (Track B4, pagination-and-ws-tenant-wide,
// growchat-doc/realtime-inbox/spec-inbox-conversation-list-pagination.md) —
// GET /manager/conversations/{tenantId} moves from a bare array response to
// an envelope `{conversations, next_cursor}` (breaking change, shipped
// together with BE — see spec's "Cursor pagination" section). This client
// normalizes both shapes into one stable `{conversations, nextCursor}`
// return value so callers never touch the raw response body:
//   - envelope shape (post-BE-cutover): reads `.conversations`/`.next_cursor`.
//   - bare array (pre-cutover / Mongo leg still on some older deploy):
//     treated as a single, final page — nextCursor is always null, matching
//     the Mongo leg's own "no cursor, no next page" contract (see spec's
//     Locked decisions #5/#6). This is deliberately kept even after the BE
//     ships the envelope everywhere, as cheap insurance against the FE/BE
//     deploy ordering the spec calls out ("code trước, KHÔNG deploy tới khi
//     BE cùng sẵn sàng") landing slightly out of sync in any one environment.
// `params.cursor`, when present, is forwarded as-is through the query
// string; omitted entirely (not sent as an empty value) when absent so a
// first-page request looks identical to how it did before pagination.
export const listConversations = (tenantId, params = {}) => {
  const { cursor, ...rest } = params
  const query = cursor ? { ...rest, cursor } : rest
  return api.get(`/manager/conversations/${tenantId}`, { params: query }).then(r => {
    const data = r.data
    if (Array.isArray(data)) {
      return { conversations: data, nextCursor: null }
    }
    return {
      conversations: Array.isArray(data?.conversations) ? data.conversations : [],
      nextCursor: data?.next_cursor ?? null,
    }
  })
}

export const getConversationDetail = (tenantId, userId) =>
  api.get(`/manager/conversations/${tenantId}/${userId}`).then(r => normalizeConversationDetail(r.data))

function normalizeContactMessages(payload) {
  if (!payload || typeof payload !== 'object') return payload
  return {
    ...payload,
    messages: Array.isArray(payload.messages) ? payload.messages : [],
    total_returned: Number(payload.total_returned || 0),
    channel_filter: payload.channel_filter || null,
    contact_id: payload.contact_id || null,
  }
}

export const getContactMessages = (tenantId, contactId, params = {}) =>
  api.get(`/manager/${tenantId}/contacts/${contactId}/messages`, { params }).then(r => normalizeContactMessages(r.data))

export const markConversationRead = (tenantId, conversationId) =>
  api.post(`/manager/conversations/${tenantId}/${conversationId}/read`).then(r => r.data)

export const listConversationStaff = (tenantId) =>
  api.get(`/manager/conversations/${tenantId}/staff/list`).then(r => {
    const data = r.data
    if (Array.isArray(data)) return data
    return Array.isArray(data?.staff) ? data.staff : []
  })

export const assignConversation = (tenantId, conversationId, staffId = null) =>
  api
    .patch(`/manager/conversations/${tenantId}/${conversationId}/assignment`, {
      staff_id: staffId || null,
    })
    .then(r => r.data)

export const toggleAI = (tenantId, userId, enabled) =>
  api.post('/admin/ai-toggle', { tenant_id: tenantId, user_id: userId, enabled }).then(r => r.data)

export const toggleAIForConversation = (tenantId, userId, enabled) =>
  api.post('/admin/ai-toggle', { tenant_id: tenantId, user_id: userId, enabled }).then(r => r.data)

// Ends a handoff so the bot replies in this conversation again. Independent
// of the AI toggle above: it neither reads nor changes ai_enabled.
export const resumeHandoffForConversation = (tenantId, userId) =>
  api.post('/admin/handoff-resume', { tenant_id: tenantId, user_id: userId }).then(r => r.data)

// Quick Answer suggestion (spec: quick-answer-pipeline, "suggestion when AI is off"):
// read it from the conversation detail, and tell the backend when staff dismissed or used it.
// Both writes return {success, cleared}; the suggestion also clears via the realtime event.
export const getQuickAnswerSuggestion = (tenantId, userId) =>
  getConversationDetail(tenantId, userId).then(detail => detail?.quick_answer_suggestion ?? null)

export const dismissQuickAnswerSuggestion = (tenantId, userId) =>
  api.post(`/manager/conversations/${tenantId}/${userId}/quick-answer-suggestion/dismiss`).then(r => r.data)

export const markQuickAnswerSuggestionUsed = (tenantId, userId) =>
  api.post(`/manager/conversations/${tenantId}/${userId}/quick-answer-suggestion/used`).then(r => r.data)

export const sendMessage = (tenantId, userId, message = '', staffId = null, imageUrl = null) => {
  const payload = { message, staff_id: staffId }
  if (imageUrl) payload.image_url = imageUrl
  return api.post(`/manager/conversations/${tenantId}/${userId}/send`, payload).then(r => r.data)
}

export const sendConversationFile = (tenantId, userId, attachment, message = '') =>
  api.post(`/manager/conversations/${tenantId}/${userId}/send-file`, {
    message,
    attachment,
  }).then(r => r.data)

// Sends 1..10 already-uploaded images as one batch, with an optional caption
// delivered before them.
//
// This resolves (never rejects) with HTTP 200 whenever the batch actually ran,
// including the case where every image failed: per-item outcomes live in
// `items[]`, so callers must read `items[].status` rather than treating a
// resolved promise as "everything sent". A rejection therefore means the batch
// never ran at all (404 unknown conversation, 403, 422 bad payload, or a
// network failure), which is the only case where rolling back the whole
// optimistic append is correct.
//
// `images[].client_ref` is echoed back on each response item and is how a
// result maps to the row it belongs to. Do not pair them up by array index:
// nothing in the contract promises the response preserves request order.
// `images[].size` lets the backend reject an oversized image from the number
// alone, with no network probe of the URL.
export const sendConversationImages = (tenantId, userId, { images, message = '' }) =>
  api.post(`/manager/conversations/${tenantId}/${userId}/send-images`, {
    images,
    message,
  }).then(r => r.data)

function resolveImageUploadFilename(file) {
  const name = file?.name || ''
  if (/\.(png|jpe?g|webp)$/i.test(name)) return name

  const extByType = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
  }
  const ext = extByType[file?.type] || 'jpg'
  const stem = name.replace(/\.[^.]+$/, '') || 'image'
  return `${stem}.${ext}`
}

export const uploadConversationImage = (tenantId, file) => {
  const form = new FormData()
  form.append('file', file, resolveImageUploadFilename(file))
  const uploadUrl = `${API_URL.replace(/\/$/, '')}/widget/${tenantId}/upload-image`
  return fetch(uploadUrl, {
    method: 'POST',
    headers: { accept: 'application/json' },
    body: form,
  }).then(async (res) => {
    let data = null
    try {
      data = await res.json()
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'parse_upload_response' }, extra: { status: res.status, tenantId } })
      data = null
    }
    if (!res.ok) {
      const error = new Error('upload image failed')
      error.response = { status: res.status, data }
      throw error
    }
    return data
  })
}

export const uploadConversationFile = (tenantId, file) => {
  const form = new FormData()
  form.append('file', file, file.name || 'attachment')
  return api.post(`/manager/conversations/${tenantId}/upload-file`, form).then(r => r.data)
}
